import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { CommonApp2dService } from '../common-app-2d-service';
import { logError } from '../../helpers/sentry.helper';
import { MODAL } from '../../modal.service';
import { CurrentService } from '../../current.service';
import { Range } from 'src/app/models/configurators-2d/applications/closet-doors-standard/range';
import { PanelGroup } from 'src/app/models/configurators-2d/applications/closet-doors-standard/panel-group';
import { Frame } from 'src/app/models/configurators-2d/applications/closet-doors-standard/frame';
import { FrameFinish } from 'src/app/models/configurators-2d/applications/closet-doors-standard/frame-finish';
import { FrameType } from 'src/app/models/configurators-2d/applications/closet-doors-standard/frame-type';
import { SaveFinishesDoor } from 'src/app/models/configurators-2d/applications/closet-doors-standard/save-finishes-door';

@Injectable({
  providedIn: 'root',
})
export class ClosetDoorsStandardService extends CommonApp2dService {
  public emplacements: any[];
  public panelGroups: PanelGroup[] = [];
  public profiles: FrameFinish[] = [];
  public frameProfiles: Frame[] = [];
  public frameTypes: FrameType[] = [];
  /** Type de matière actuellement sélectionné (Acier / Aluminium). */
  public selectedFrameType: FrameType | null = null;
  /** Profilé actuellement sélectionné (C3, C4, ALU…). */
  public selectedFrameProfile: Frame | null = null;
  public structures: any[];
  public layers: any[];
  public decorations: any[];

  private _globalMinWidth = 0;
  private _globalMaxWidth = 0;

  /** Largeur minimale possible tous panels confondus. */
  get globalMinWidth(): number {
    return this._globalMinWidth;
  }

  /** Largeur maximale possible tous panels confondus. */
  get globalMaxWidth(): number {
    return this._globalMaxWidth;
  }

  /**
   * Charge une fois les bornes absolues de largeur depuis tous les panels disponibles.
   * À appeler à l'initialisation du composant de configuration des vantaux.
   */
  async loadGlobalBounds(): Promise<void> {
    const groups = await this.getPanels();
    const allPanels = groups.flatMap(g => g.panels);
    if (allPanels.length > 0) {
      this._globalMinWidth = Math.min(...allPanels.map(p => p.minWidth));
      this._globalMaxWidth = Math.max(...allPanels.map(p => p.maxWidth));
    }
  }

  protected readonly current = inject(CurrentService);

  constructor() {
    super(CONFIG_CONTEXT.CLOSET_DOORS_STANDARD);
  }

  /**
   * Récupère les groupes de vantaux disponibles filtrés par les dimensions de la baie.
   * Retourne les combinaisons (pourcentage → nombres de vantaux) compatibles avec
   * la hauteur et la largeur saisies.
   */
  getPanels(height?: number, width?: number): Promise<PanelGroup[]> {
    let params = new HttpParams();
    if (height != null) params = params.set('height', height);
    if (width != null) params = params.set('width', width);
    return this.get(this.apiUrl + 'Configuration/Panels', params);
  }

  /**
   * Fonction de récupération des accessoires
   * @returns environnements
   */
  getAccessories() {
    return this.get(this.apiUrl + 'Configuration/Accessories');
  }

  /**
   * Création d'une configuration pour un emplacement sélectionné
   * @param emplacementId id de l'emplacement sélectionné
   * @param hasDrawers boolean y a t il des tiroirs
   * @param drawersLocation localisation des tiroirs
   * @returns id de la configuration
   */
  createConfigurationByEmplacement(title: string, emplacementId: number, hasDrawers: boolean, drawersLocation: number) {
    const params = new HttpParams()
      .set('title', title)
      .set('locationId', emplacementId)
      .set('hasDrawers', hasDrawers)
      .set('DrawerLocations', drawersLocation);
    return this.post(this.apiUrl + 'Configuration/SaveConfiguration', undefined, params);
  }

  /**
   * Fonction de création d'une configuration par le titre
   * @param title titre de la configuration
   * @returns id de la configuration fraichement créée
   */
  createConfigurationByTitle(title: string) {
    const params = new HttpParams().set('title', title);
    return this.post(this.apiUrl + 'Configuration/SaveConfigurationTitle', null, params);
  }

  /**
   * Récupère toutes les matières de profilé disponibles (Acier, Aluminium…), ordonnées par DisplayOrder.
   */
  getFrameTypes(): Promise<FrameType[]> {
    return this.get(this.apiUrl + 'Configuration/FrameTypes');
  }

  /**
   * Récupère les profilés disponibles pour une matière donnée,
   * filtrés selon les types de porte sélectionnés et les dimensions de la configuration.
   * Un profilé n'est retourné que s'il possède des DFCs compatibles pour CHACUN des doorTypeIds fournis.
   * @param frameTypeId ID de la matière (FrameType) pour filtrer les profilés
   * @param configId ID de la configuration (pour filtrer selon les dimensions et la gamme)
   * @param doorTypeIds IDs des types de porte actuellement sélectionnés
   * @param rangeId ID de gamme optionnel — surcharge la gamme sauvegardée (usage preview)
   */
  getFrames(frameTypeId: number, configId: string, doorTypeIds: number[], rangeId?: number): Promise<Frame[]> {
    let params = new HttpParams()
      .set('id', configId)
      .set('frameTypeId', frameTypeId);
    doorTypeIds.forEach(id => (params = params.append('doorTypeIds', id)));
    if (rangeId != null) params = params.set('rangeId', rangeId);
    return this.get(this.apiUrl + 'Configuration/Frames', params);
  }

  /**
   * Fonction de récupération des structures
   * @param configId ID de la configuration
   * @param rangeId ID de gamme optionnel — filtre les structures sans sauvegarder (usage preview)
   * @param frameId ID de profilé optionnel — filtre les finitions par profilé (C3, C4, ALU…)
   * @returns structures filtrées par gamme et/ou profilé si fournis, sinon par la gamme sauvegardée
   */
  getStructures(configId: string, rangeId?: number, frameId?: number): Promise<unknown> {
    let params = new HttpParams().set('id', configId);
    if (rangeId != null) params = params.set('rangeId', rangeId);
    if (frameId != null) params = params.set('frameId', frameId);
    return this.get(this.apiUrl + 'Configuration/FrameFinishes', params);
  }

  /**
   * Fonction de récupération des décors
   * @returns décors
   */
  async getDecorations(configurationId?: string) {
    try {
      const url = configurationId
        ? `${this.apiUrl}Configuration/Colors?configurationId=${configurationId}`
        : `${this.apiUrl}Configuration/Colors`;
      const res = await this.get(url);
      return res.colors;
    } catch {
      return undefined;
    }
  }

  /**
   * Fonction de changement du titre d'une configuration
   * @param id id de la configuration
   * @param title titre de la configuration à changer
   * @returns /
   */
  saveTitle(id: string, title: string) {
    const params = new HttpParams().set('id', id).set('title', title);
    return this.post(this.apiUrl + 'Configuration/SaveTitle', {}, params);
  }

  /**
   * Sauvegarde l'emplacement (environnement) de la configuration.
   * @param id id de la configuration
   * @param locationId id de l'emplacement sélectionné
   * @param hasDrawers y a-t-il des tiroirs
   * @param drawerLocation localisation des tiroirs
   */
  saveEnvironment(id: string, locationId: number, hasDrawers: boolean, drawerLocation: number): Promise<unknown> {
    const params = new HttpParams()
      .set('id', id)
      .set('locationId', locationId)
      .set('hasDrawer', hasDrawers)
      .set('DrawerLocations', drawerLocation);
    return this.post(this.apiUrl + 'Configuration/SaveEnvironment', {}, params);
  }

  /**
   * Sauvegarde les dimensions et le nombre de vantaux de la configuration.
   * @param id id de la configuration
   * @param height hauteur des vantaux
   * @param width largeur des vantaux
   * @param hasPlinth y a-t-il des plinthes
   * @param panelId panel
   */
  saveDimensions(
    id: string,
    height: number,
    width: number,
    hasPlinth: boolean,
    needBaseboardCompensator: boolean,
    baseboardCompensatorQty: number,
    panelId: number
  ): Promise<unknown> {
    const params = new HttpParams()
      .set('id', id)
      .set('height', height)
      .set('width', width)
      .set('baseboard', hasPlinth)
      .set('needBaseboardCompensator', needBaseboardCompensator)
      .set('baseboardCompensatorQty', baseboardCompensatorQty)
      .set('panelId', panelId);
    return this.post(this.apiUrl + 'Configuration/SaveDimensions', {}, params, undefined, false).catch(err => {
      let modal = MODAL.GENERIC_ERROR;
      if (err?.error?.message === 'ERRST01') {
        if (!this.current.vendor) {
          modal = MODAL.GENERIC_ERROR_CUSTOMER;
          err.error.message = 'Pour ces dimensions, le format standard est la solution la plus adaptée.';
        } else {
          modal = MODAL.GENERIC_ERROR;
          err.error.message =
            'Les dimensions renseignées sont standards, rendez-vous dans COM+ pour passer votre commande.';
        }
      }
      this.modalService.display(modal, {
        errorMessage: this.errorToMessage(err),
      });
      throw err;
    });
  }

  /**
   * Sauvegarde la gamme sélectionnée pour la configuration.
   * @param configId id de la configuration
   * @param rangeId id de la gamme sélectionnée
   */
  saveRange(configId: string, rangeId: number): Promise<unknown> {
    const params = new HttpParams().set('id', configId).set('rangeId', rangeId);
    return this.post(this.apiUrl + 'Configuration/SaveRange', {}, params);
  }

  /**
   * Sauvegarde consolidée et atomique de l'étape Finitions : profilé commun + type de porte
   * et coloris des sections de chaque vantail, en une seule requête.
   * Remplace l'enchaînement SaveProfile / SaveDoorType / SaveDecoration afin d'éviter les
   * requêtes asynchrones concurrentes qui créaient des sections en doublon.
   * @param id id du projet
   * @param frameFinishId id de la finition de profilé commune (FrameFinish.Id)
   * @param doors finitions de chaque vantail
   */
  saveFinishes(id: string, frameFinishId: number, doors: SaveFinishesDoor[]): Promise<void> {
    return this.post<void>(this.apiUrl + 'Configuration/SaveFinishes', { id, frameFinishId, doors });
  }

  /**
   * Fonction de récupération du résumé
   * @param id id du projet
   * @returns résumé
   */
  getResume(id: string) {
    const params = new HttpParams().set('id', id);
    return this.get(this.apiUrl + 'Configuration/Resume', params);
  }

  /**
   * Fonction de récupération d'une configuration par son id
   * @param id id de la configuration
   * @returns projet
   */
  getProjectById(id: string) {
    const params = new HttpParams().set('id', id);
    return this.get(this.apiUrl + 'Configuration', params);
  }

  /**
   * Fonction de récupération du devis lié au projet
   * @param id identifiant du projet
   * @returns devis
   */
  getQuotation(id: string): Promise<any> {
    const params = new HttpParams().set('id', id);
    return this.get(this.apiUrl + 'Order/Quotation', params);
  }

  /**
   * Création du devis pour la configuration (pas de création si devis déjà en cours)
   * @param id identifiant du projet
   * @returns identifiant du devis
   */
  setQuotation(id: string): Promise<any> {
    const params = new HttpParams().set('id', id);
    return this.get(this.apiUrl + 'Order/CreateQuotation', params);
  }

  /**
   * Fonction de sauvegarde du visuel de la configuration
   * @param id id de la configuration
   * @param b64Img visuel de la configuration encodé en base64
   * @returns promise
   */
  saveVisual(id: string, thumbnail: { id?: string; data: string }) {
    return this.post(this.apiUrl + 'Configuration/SaveVisual', { id, thumbnail });
  }

  /**
   * Fonction d'assignation d'un client à une configuration
   * @param id id de la configuration
   * @returns promise
   */
  async assignCustomer(id: string): Promise<boolean> {
    try {
      const params = new HttpParams().set('id', id);
      await this.post(this.apiUrl + 'Configuration/AssignCustomer', {}, params);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Construit le payload de l'endpoint consolidé `SaveFinishes` à partir des vantaux en mémoire :
   * un vantail par index (1-based), avec son type de porte et le coloris de chaque section
   * configurable (non fixe) ayant une couleur choisie.
   * @param doors vantaux courants de la configuration
   */
  buildFinishesPayload(doors: any[]): SaveFinishesDoor[] {
    return (doors ?? []).map((d: any, i: number) => ({
      panelIndex: i + 1,
      doorTypeId: d?.structure?.id,
      sections: (d?.sections ?? [])
        .filter((s: any) => !s.isFixed && s.color?.id !== undefined)
        .map((s: any) => ({ sectionIndex: s.index, colorId: s.color.id })),
    }));
  }

  override async saveProject(configuration: any): Promise<boolean> {
    const confId = configuration.id;
    const selectedEmplacementId = configuration.selectedEmplacement?.id;
    const doorNumberId = this.panelGroups.flatMap(g => g.panels).find(p => p.panelCount === configuration.doorNumber)?.id ?? null;

    try {
      await this.saveEnvironment(
        confId,
        selectedEmplacementId,
        configuration?.hasDrawers,
        configuration?.drawersLocation
      );

      if (doorNumberId != null) {
        await this.saveDimensions(
          confId,
          configuration?.doorHeight,
          configuration?.doorWidth,
          configuration?.hasPlinth,
          configuration?.needBaseboardCompensator,
          configuration?.baseboardCompensatorQty,
          doorNumberId
        );

        if (configuration.selectedRange?.id != null) {
          await this.saveRange(confId, configuration.selectedRange.id);
        }

        if (configuration.frameFinish?.id) {
          // Sauvegarde consolidée et atomique : profilé + types de porte + coloris des sections
          // en une seule requête, pour éviter les doublons dus aux appels concurrents.
          await this.saveFinishes(confId, configuration.frameFinish.id, this.buildFinishesPayload(configuration.doors));
        }
      }

      return this.assignCustomer(configuration.id);
    } catch (err) {
      logError(err);
      throw err;
    }
  }

  /**
   * Récupère les gammes compatibles avec la configuration courante.
   * Le back filtre selon les types de porte disponibles pour les dimensions saisies.
   * @param configId identifiant de la configuration
   */
  getRanges(configId: string): Promise<Range[]> {
    const params = new HttpParams().set('id', configId);
    return this.get(this.apiUrl + 'Configuration/Ranges', params);
  }

  /**
   * Fonction de récupération des doorTypes
   * @param familyId ID de la marque des portes
   */
  getDoorTypes(familyId: number): Promise<any> {
    const params = new HttpParams().set('familyId', familyId);
    return this.get(this.apiUrl + 'Configuration/DoorTypes', params);
  }

  /**
   * Applique le résultat d'un appel `getStructures` : met à jour les profils, structures et calques,
   * puis recrée ou réinitialise le tableau des portes selon le nombre de vantaux.
   * @param res réponse brute de l'API (frames, profiles, layers)
   */
  applyStructuresResult(res: any): void {
    this.profiles = res.frameFinishes;
    const existingProfile = this.current.closetDoors.frameFinish;
    this.current.closetDoors.frameFinish =
      (existingProfile ? this.profiles.find((p: FrameFinish) => p.id === existingProfile.id) : null)
      ?? this.profiles.find((p: FrameFinish) => p.isDefault)
      ?? this.profiles[0]
      ?? null;

    this.structures = res.doors;
    this.layers = res.layers;

    const rangeDefaultDoorTypeId = (this.current.closetDoors as any).range?.defaultDoorTypeId;
    const defaultStructure =
      this.structures.find((s: any) => s.id === rangeDefaultDoorTypeId)
      ?? this.structures.find((s: any) => s.isDefault === true)
      ?? this.structures[0];
    const profile = this.current.closetDoors.frameFinish;

    const buildSections = (structure: any) =>
      (structure?.sections ?? []).map((s: any) => ({
        index: s.sectionIndex,
        heightPercentage: s.heightPercentage,
        isFixed: s.isFixed,
        fixedColorId: s.fixedColorId ?? null,
        // Couleur initiale : pour les sections fixes, on cherche la Color dans decorations
        color: s.fixedColorId
          ? (this.decorations?.find((d: any) => d.id === s.fixedColorId) ?? null)
          : null,
        availableColorIds: s.availableColorIds ?? [],
        linkedSectionIndexes: s.linkedSectionIndexes ?? [],
      }));

    const buildDoor = (index: number, prevSections: any[] = []) => {
      const structure = defaultStructure;
      const sections = buildSections(structure).map((s: any) => {
        // Conserver la couleur choisie si la section existait déjà et que la couleur est compatible
        const prev = prevSections.find((ps: any) => ps.index === s.index);
        const colorIsCompatible = prev?.color && (
          s.availableColorIds.length === 0 ||
          s.availableColorIds.includes(prev.color.id)
        );
        return s.isFixed ? s : { ...s, color: colorIsCompatible ? prev.color : null };
      });
      return {
        id: index + 1,
        structure,
        layer: this.layers.find(
          (l: any) => l.frameFinishId === profile?.id && l.doorTypeId === structure?.id
        ),
        sections,
      };
    };

    const doors: any[] = this.current.closetDoors.doors;
    const doorNumber: number = (this.current.closetDoors as any).doorNumber;

    if (doorNumber !== doors.length) {
      this.current.closetDoors.doors = Array.from({ length: doorNumber }, (_, i) => buildDoor(i));
    } else {
      this.current.closetDoors.doors = doors.map((door: any, i: number) => {
        const structureStillAvailable = door.structure != null && res.doors.some((f: any) => f.id === door.structure.id);
        return structureStillAvailable ? door : buildDoor(i, door.sections ?? []);
      });
    }

    this.updateLayers();

    // Si un vantail n'a toujours pas de calque après updateLayers (ex. : changement de matière
    // vers une gamme qui ne supporte pas la structure précédente), on le reconstruit avec la
    // structure par défaut de la nouvelle matière.
    this.current.closetDoors.doors = this.current.closetDoors.doors.map((door: any, i: number) =>
      door.layer ? door : buildDoor(i, door.sections ?? [])
    );
  }

  /**
   * Met à jour le calque de rendu de chaque porte selon la finition et la structure actives.
   */
  updateLayers(): void {
    const profileId = this.current.closetDoors.frameFinish?.id;
    for (const door of this.current.closetDoors.doors) {
      door.layer = this.layers?.find(
        (l: any) => l.frameFinishId === profileId && l.doorTypeId === door.structure?.id
      );
    }
  }
}
